import { httpClient } from '@bs/core'
import qs from 'querystring'
import type { ParsedUrlQueryInput } from 'querystring'
import type {
  BulkAssignPayload,
  Product,
  ProductDraft,
  LinkedProduct,
  ProductExportResponse,
  ProductListParams,
  ProductListResponse,
  StockLevel,
  ProductLocationStock,
  ProductVariant,
  VariationGroupItem,
} from '../../../types/product'
import { mapListVariations } from './mapVariations'
import { parsePageScore, parsePageScoreAreas } from './parsePageScore'

const base = (storeId: string) => `/greenline/stores/${storeId}/products`
const detail = (storeId: string, productId: string) => `/greenline/stores/${storeId}/products/${productId}`
const bulkUrl = (storeId: string) => `/greenline/stores/${storeId}/products`
const exportUrl = (storeId: string) => `/greenline/stores/${storeId}/products/export`

function sanitizeVariationData(variations: VariationGroupItem[]): VariationGroupItem[] {
  return variations.map((v) => {
    const sanitized = {
      ...v,
      image: v.image ?? {},
      volume_unit: 'ml',
    }
    if (Array.isArray(sanitized.locations)) {
      sanitized.locations = sanitized.locations.map((loc) => ({
        ...loc,
        image: loc.image ?? {},
        // Specification fields are always mirrored from the variation item into every location
        plant_type: v.plant_type,
        thc_min: v.thc_min,
        thc_max: v.thc_max,
        thc_unit: v.thc_unit,
        cbd_min: v.cbd_min,
        cbd_max: v.cbd_max,
        cbd_unit: v.cbd_unit,
        cbn_min: v.cbn_min,
        cbn_max: v.cbn_max,
        cbn_unit: v.cbn_unit,
        cbg_min: v.cbg_min,
        cbg_max: v.cbg_max,
        cbg_unit: v.cbg_unit,
        terpenes: v.terpenes,
        terpenes_total: v.terpenes_total,
        terpenes_total_unit: v.terpenes_total_unit,
        effects: v.effects,
        flavours: v.flavours,
        bs_custom_specifications: v.bs_custom_specifications,
        volume: v.volume,
        volume_unit: 'ml',
        weight: v.weight,
        cannabis_weight: v.cannabis_weight,
        description: v.description,
        gtin: v.gtin,
        upc: v.upc,
        _custom_product_meta_gtin: v._custom_product_meta_gtin ?? null,
        _custom_product_meta_upc: v._custom_product_meta_upc ?? null,
      }))
    }
    return sanitized
  })
}

function resolveStock(qty: number): StockLevel {
  if (qty <= 0) return 'out'
  return 'in'
}

function mapVariantFromApi(v: Record<string, unknown>): ProductVariant {
  const locations = (v.locations as Record<string, unknown>[] | undefined) ?? []
  const firstLoc = locations[0] as Record<string, unknown> | undefined
  const stockQty = (v.total_available as number) ?? 0
  const price = parseFloat((firstLoc?.regular_price as string) ?? '0') || 0
  const salePrice = firstLoc?.sale_price ? parseFloat(firstLoc.sale_price as string) : undefined

  const cogsRaw = v.cost_of_goods ?? v.cogs
  const cogs = cogsRaw != null ? parseFloat(String(cogsRaw)) : undefined

  return {
    id: String(v._id ?? v.id ?? ''),
    name: String(v.name ?? ''),
    sku: String(v.short_sku ?? ''),
    price,
    salePrice,
    cogs: Number.isFinite(cogs) ? cogs : undefined,
    stock: resolveStock(stockQty),
    stockQty,
    hcClass: undefined,
    isCurrent: false,
    availability: undefined,
  }
}

function mapLocationStockFromApi(
  quantityMap: Record<string, { stock_quantity?: number; threshold?: number; location_name?: string }>,
): ProductLocationStock[] {
  return Object.entries(quantityMap).map(([locationId, loc]) => ({
    locationId,
    locationName: loc.location_name ?? locationId,
    stockQty: loc.stock_quantity ?? 0,
    threshold: loc.threshold ?? 0,
  }))
}

function mapLinkedProductsFromApi(raw: unknown): LinkedProduct[] {
  if (!Array.isArray(raw)) return []
  return (raw as Record<string, unknown>[]).map((item) => ({
    id: String(item.id ?? item._id ?? ''),
    name: String(item.name ?? ''),
    sku: item.sku ? String(item.sku) : undefined,
    price: item.price != null ? parseFloat(String(item.price)) : undefined,
    stock_quantity: item.stock_quantity != null ? Number(item.stock_quantity) : undefined,
    images: Array.isArray(item.images)
      ? (item.images as Record<string, unknown>[]).map((img) => ({ src: String(img.src ?? '') }))
      : undefined,
  }))
}

function mapFromApi(item: Record<string, unknown>): Product {
  const quantity = (item.quantity as Record<string, unknown>) ?? {}
  const stockQty: number = (quantity.total_stock_quantity as number) ?? 0
  const locations = quantity.locations
    ? mapLocationStockFromApi(quantity.locations as Record<string, { stock_quantity?: number; threshold?: number; location_name?: string }>)
    : []

  const categories = (item.categories as { id: string | number; name?: string }[]) ?? []
  const primaryCat = categories[0]

  const singularImage = item.image as Record<string, unknown> | undefined
  const pluralImages = (item.images as Record<string, unknown>[]) ?? []
  const images: Product['images'] = singularImage?.src
    ? [{ id: String(singularImage.id ?? '0'), src: String(singularImage.src), alt: String(singularImage.alt ?? ''), caption: String(singularImage.caption ?? ''), isFeatured: true, srcset: (singularImage.srcset as Record<string, string>) ?? undefined }]
    : pluralImages.map((img, idx) => ({
        id: String(img.id ?? idx),
        src: String(img.src ?? ''),
        alt: String(img.alt ?? ''),
        caption: String(img.caption ?? ''),
        isFeatured: idx === 0,
        srcset: (img.srcset as Record<string, string>) ?? undefined,
      }))

  const rawStatus = String(item.bs_status ?? item.status ?? 'draft')
  const bsStatus = (rawStatus === 'trash' ? 'archived' : rawStatus) as Product['status']
  const scheduledAt = bsStatus === 'scheduled' && item.date_created_gmt ? String(item.date_created_gmt) : undefined

  return {
    id: String(item.id ?? item._id ?? ''),
    name: String(item.name ?? ''),
    slug: String(item.slug ?? ''),
    sku: String(item.short_sku ?? ''),
    brand: item.brand ? String(item.brand) : undefined,
    categoryId: primaryCat?.id != null ? String(primaryCat.id) : undefined,
    categoryIds: categories.map((c) => String(c.id)),
    categoryName: primaryCat?.name,
    subCategories: categories.slice(1).map((c) => c.name ?? ''),
    price: parseFloat(String(item.price ?? '0')) || 0,
    minPrice: item.min_price != null ? parseFloat(String(item.min_price)) : undefined,
    maxPrice: item.max_price != null ? parseFloat(String(item.max_price)) : undefined,
    salePrice: item.sale_price ? parseFloat(String(item.sale_price)) : undefined,
    cogs: item.cost_of_goods != null ? parseFloat(String(item.cost_of_goods)) : item.cogs != null ? parseFloat(String(item.cogs)) : undefined,
    status: bsStatus,
    stock: resolveStock(stockQty),
    stockQty,
    locationStock: locations,
    variations: mapListVariations(item.variation_data, categories, item.brand, resolveStock),
    mapped: !(item.pos_disconnect_by_category as boolean),
    images,
    shortDescription: String(item.short_description ?? ''),
    longDescription: String(item.description ?? ''),
    tags: ((item.tags as (string | number | { name: string })[]) ?? []).map((t) => {
      if (typeof t === 'number') return t
      if (typeof t === 'string') return t
      return t.name
    }),
    // Greenline POS product name lives on the detail response's variation_data
    // (variation_data[0].gl_product_name). It is the POS-side name, distinct from
    // the storefront item.name — do NOT fall back to item.name. Optional-chain so
    // a missing payload yields '' (renders '—') rather than crashing.
    posProductName: String(
      (item.variation_data as Record<string, unknown>[] | undefined)?.[0]?.gl_product_name ?? '',
    ),
    posSku: String(item.short_sku ?? ''),
    posStatus: String(item.pos_status ?? 'syncing'),
    waitingSyncingFromPos: Boolean(item.waiting_syncing_from_pos),
    posIntegration: 'greenline',
    updatedAt: String(item.date_modified_gmt ?? ''),
    createdAt: String(item.date_created_gmt ?? ''),
    publishedAt: item.date_published ? String(item.date_published) : undefined,
    date_created_gmt: item.date_created_gmt ? String(item.date_created_gmt) : undefined,
    scheduledAt,
    upsellIds: Array.isArray(item.upsell_ids) ? (item.upsell_ids as string[]) : [],
    relatedIds: Array.isArray(item.related_ids) ? (item.related_ids as string[]) : [],
    upsellData: mapLinkedProductsFromApi(item.upsell_data),
    relatedData: mapLinkedProductsFromApi(item.related_data),
    seoTitle: (item.seo as Record<string, string> | undefined)?.title ?? undefined,
    seoDescription: (item.seo as Record<string, string> | undefined)?.meta_description ?? undefined,
    canonicalUrl: (item.seo as Record<string, string> | undefined)?.canonical_url ?? undefined,
    seoKeyphrase: (item.seo as Record<string, string> | undefined)?.focus_keyphrase ?? '',
    aiCredits: item.ai_credits
      ? {
          used: (item.ai_credits as Record<string, number>).used ?? 0,
          remaining: (item.ai_credits as Record<string, number>).remaining ?? 0,
          total: (item.ai_credits as Record<string, number>).total ?? 0,
        }
      : undefined,
    locationSpecificData: item.location_specific_data != null ? Boolean(item.location_specific_data) : undefined,
    pageScore: parsePageScore(item.page_score),
    pageScoreAreas: parsePageScoreAreas(item.page_score_data),
  }
}

function buildListParams(params: ProductListParams): Record<string, unknown> {
  const p: Record<string, unknown> = {
    page: params.page,
    limit: params.limit,
  }
  if (params.sortBy) { p.sort_by = params.sortBy; p.sort = params.sort ?? -1 }
  if (params.status && params.status !== 'all') p.status = params.status === 'archived' ? 'trash' : params.status
  if (params.search) p.search = params.search
  if (params.locationId) p.location_id = params.locationId
  // Arrays serialize as repeated keys (`category_ids=1&category_ids=2`) via
  // `querystring.stringify` — the DRF `ListField` reads them with `getlist`.
  // Comma-joined values would fail the IntegerField child validation. Sorted
  // so the same selection always produces the same request.
  if (params.categoryIds?.length) p.category_ids = [...params.categoryIds].sort()
  if (params.brandIds?.length) p.brands = [...params.brandIds].sort()
  if (params.stockQuantityMin !== undefined) p.stock_quantity_min = params.stockQuantityMin
  if (params.stockQuantityMax !== undefined) p.stock_quantity_max = params.stockQuantityMax
  // Exact match on the backend — 'syncing' won't include Budsense-linked
  // products (pos_status: 'budsense'), unlike the column's binary display.
  if (params.posStatus) p.pos_status = params.posStatus
  // Arrays -> repeated `page_score_states=` / `page_score_areas=` keys via
  // querystring.stringify; empty lists are omitted.
  if (params.pageScoreStates?.length) p.page_score_states = params.pageScoreStates
  if (params.pageScoreAreas?.length) p.page_score_areas = params.pageScoreAreas
  return p
}

function mapListResponse(raw: Record<string, unknown>): ProductListResponse {
  const items = ((raw.items as Record<string, unknown>[]) ?? []).map(mapFromApi)
  return {
    data: items,
    totalItems: (raw.total_items as number) ?? items.length,
    totalPages: (raw.total_pages as number) ?? 1,
    all: (raw.all as number) ?? items.length,
    published: (raw.published as number) ?? 0,
    draft: (raw.draft as number) ?? 0,
    archived: (raw.trash as number) ?? 0,
    pageScoreNeedsImprovement: parsePageScore(raw.page_score_needs_improvement),
  }
}

export const greenlineProductApis = {
  getList: async (storeId: string, params: ProductListParams): Promise<ProductListResponse> => {
    const raw = (await httpClient.json.get(
      `${base(storeId)}?${qs.stringify(buildListParams(params) as ParsedUrlQueryInput)}`,
    )) as Record<string, unknown>
    return mapListResponse(raw)
  },

  getDetail: async (storeId: string, productId: string): Promise<Product> => {
    const raw = (await httpClient.json.get(detail(storeId, productId))) as Record<string, unknown>
    return mapFromApi(raw)
  },

  getVariationGroup: async (storeId: string, productId: string): Promise<VariationGroupItem[]> => {
    const raw = (await httpClient.json.get(
      `${detail(storeId, productId)}/variation_group`,
    )) as { data?: VariationGroupItem[] } | VariationGroupItem[] | null
    if (!raw) return []
    if (Array.isArray(raw)) return raw
    return (raw as { data?: VariationGroupItem[] }).data ?? []
  },

  update: async (storeId: string, productId: string, data: Partial<ProductDraft>): Promise<Product> => {
    // external_datasource is always sent as 'manual' on update — once a product has been
    // edited from the Pantry UI we treat it as manually-managed, regardless of the prior
    // datasource. Backend (be_libs/utils/greenline/products.py) reads this field to decide
    // whether POS sync may overwrite the doc.
    const payload: Record<string, unknown> = {
      pos_status: 'disconnect',
      external_datasource: 'manual',
    }
    if (data.name !== undefined) payload.name = data.name
    if (data.slug !== undefined) payload.slug = data.slug
    if (data.shortDescription !== undefined) payload.short_description = data.shortDescription
    if (data.longDescription !== undefined) payload.description = data.longDescription
    if (data.status !== undefined) payload.bs_status = data.status === 'archived' ? 'trash' : data.status
    if (data.status === 'scheduled' && data.scheduledAt) {
      payload.date_created_gmt = data.scheduledAt
    }
    if (data.images !== undefined) {
      payload.images = data.images.map((img) => ({ id: img.id, src: img.src, alt: img.alt ?? '', caption: img.caption ?? '' }))
    }
    if (data.tags !== undefined) {
      payload.tags = data.tags.map((t) => (typeof t === 'number' ? t : { name: String(t) }))
    }
    if (data.categoryIds !== undefined) payload.categories = data.categoryIds.map((id) => ({ id: Number(id) }))
    if (data.brand !== undefined) payload.brand = data.brand ? Number(data.brand) : null
    if (data.variationData !== undefined) {
      payload.variation_data = sanitizeVariationData(data.variationData)
    } else if (data.upc !== undefined || data.gtin !== undefined) {
      const fallback: Record<string, unknown> = {}
      if (data.upc !== undefined) fallback._custom_product_meta_upc = data.upc
      if (data.gtin !== undefined) fallback._custom_product_meta_gtin = data.gtin
      payload.variation_data = [fallback]
    }
    if (data.upsellIds !== undefined) payload.upsell_ids = data.upsellIds
    if (data.relatedIds !== undefined) {
      payload.related_ids = data.relatedIds
        .map((id) => Number(id))
        .filter((n) => Number.isFinite(n))
    }
    if (data.locationSpecificData !== undefined) payload.location_specific_data = data.locationSpecificData
    // Always send the full `seo` object so a save never drops a key the
    // backend already stores.
    if (
      data.seoTitle !== undefined ||
      data.seoDescription !== undefined ||
      data.canonicalUrl !== undefined ||
      data.seoKeyphrase !== undefined
    ) {
      payload.seo = {
        title: data.seoTitle ?? '',
        meta_description: data.seoDescription ?? '',
        canonical_url: data.canonicalUrl ?? '',
        focus_keyphrase: data.seoKeyphrase ?? '',
      }
    }

    const raw = (await httpClient.json.put(detail(storeId, productId), payload)) as Record<string, unknown>
    return mapFromApi(raw)
  },

  // Triggers a POS resync — PUTs the same detail endpoint as `update()` but with only
  // `external_datasource: 'pos'`, matching the old ConnectDialog "sync" flow exactly.
  // Deliberately not routed through `update()`, which always forces 'manual'.
  syncFromPos: async (storeId: string, productId: string): Promise<Product> => {
    const raw = (await httpClient.json.put(detail(storeId, productId), {
      external_datasource: 'pos',
    })) as Record<string, unknown>
    return mapFromApi(raw)
  },

  bulkUpdate: async (storeId: string, ids: string[], action: string): Promise<void> => {
    const bsStatus = action === 'publish' ? 'published' : action === 'draft' ? 'draft' : action === 'archive' ? 'trash' : action
    await httpClient.json.put(bulkUrl(storeId), { ids, bs_status: bsStatus })
  },

  // Bulk "Map" — assign category/brand to many products in one request. Uses the
  // real bulk endpoint's {store_id, organization_id, product_ids} envelope plus the
  // per-product `categories`/`brand` field names.
  bulkAssign: async (storeId: string, orgId: string, productIds: string[], payload: BulkAssignPayload): Promise<void> => {
    await httpClient.json.put(bulkUrl(storeId), {
      store_id: storeId,
      organization_id: orgId,
      // Backend expects numeric product ids; selection ids are strings.
      product_ids: productIds.map((id) => Number(id)),
      // Always sent on bulk assign — once products are edited from Pantry they are
      // treated as manually-managed so POS sync won't overwrite the assignment.
      external_datasource: 'manual',
      ...(payload.categories !== undefined && { categories: payload.categories }),
      ...(payload.brand !== undefined && { brand: payload.brand }),
    })
  },

  exportCsv: async (storeId: string, params: ProductListParams): Promise<ProductExportResponse> => {
    // Export operates on the full filtered set, not the current page — send
    // empty page/limit (matches the legacy `...export?page=&limit=&...` call)
    // so the backend decides inline-vs-async by result size.
    const p = buildListParams(params)
    p.page = ''
    p.limit = ''
    if (params.timeZone !== undefined) p.time_zone = params.timeZone
    return httpClient.json.get(
      `${exportUrl(storeId)}?${qs.stringify(p as ParsedUrlQueryInput)}`,
    ) as Promise<ProductExportResponse>
  },

  searchForLinking: async (
    storeId: string,
    excludeProductId: string,
    term: string,
  ): Promise<LinkedProduct[]> => {
    const p: Record<string, unknown> = {
      limit: 100,
      page: 1,
      search: term.trim(),
      sort: 1,
      sort_by: 'date_created_gmt',
      exclude_product_ids: excludeProductId,
    }
    const raw = (await httpClient.json.get(
      `${base(storeId)}?${qs.stringify(p as ParsedUrlQueryInput)}`,
    )) as Record<string, unknown>
    const items = (raw.items as Record<string, unknown>[]) ?? []
    return items.map((item) => {
      const quantity = (item.quantity as Record<string, unknown>) ?? {}
      const singularImage = item.image as Record<string, unknown> | undefined
      const pluralImages = (item.images as Record<string, unknown>[]) ?? []
      const images = singularImage?.src
        ? [{ src: String(singularImage.src) }]
        : pluralImages.map((img) => ({ src: String(img.src ?? '') }))
      return {
        id: String(item.id ?? item._id ?? ''),
        name: String(item.name ?? ''),
        sku: item.short_sku ? String(item.short_sku) : undefined,
        price: item.price != null ? parseFloat(String(item.price)) : undefined,
        stock_quantity: (quantity.total_stock_quantity as number) ?? undefined,
        images: images.length > 0 ? images : undefined,
      }
    })
  },
}
